import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/auth";

const IDLE_MS      = 30 * 60 * 1000; // 30 menit idle → tampilkan warning
const WARNING_SEC  = 120;             // 2 menit countdown sebelum logout

function getTokenExpiry(): number | null {
    const token = localStorage.getItem("ns_token");
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}

export function useIdleTimeout() {
    const router = useRouter();
    const auth   = useAuthStore();

    const showWarning = ref(false);
    const countdown   = ref(WARNING_SEC);

    let idleTimer:      ReturnType<typeof setTimeout>  | null = null;
    let countdownTimer: ReturnType<typeof setInterval> | null = null;

    function clearAll() {
        if (idleTimer)      clearTimeout(idleTimer);
        if (countdownTimer) clearInterval(countdownTimer);
        idleTimer      = null;
        countdownTimer = null;
        showWarning.value = false;
        countdown.value   = WARNING_SEC;
    }

    function doLogout(reason: "idle" | "expired") {
        clearAll();
        auth.logout();
        router.push({ name: "LoginPage", query: { reason } });
    }

    function startCountdown() {
        showWarning.value = true;
        countdown.value   = WARNING_SEC;
        countdownTimer = setInterval(() => {
            countdown.value--;
            if (countdown.value <= 0) doLogout("idle");
        }, 1000);
    }

    function resetTimer() {
        if (!auth.isAuthenticated) return;

        // Cek apakah JWT sudah expired
        const exp = getTokenExpiry();
        if (exp && Date.now() >= exp) {
            doLogout("expired");
            return;
        }

        // Kalau warning sedang tampil dan user aktif → dismiss
        if (showWarning.value) clearAll();

        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(startCountdown, IDLE_MS);
    }

    function stayLoggedIn() {
        clearAll();
        resetTimer();
    }

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

    onMounted(() => {
        if (!auth.isAuthenticated) return;
        resetTimer();
        events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    });

    onUnmounted(() => {
        clearAll();
        events.forEach(e => window.removeEventListener(e, resetTimer));
    });

    return { showWarning, countdown, stayLoggedIn, doLogout: () => doLogout("idle") };
}
